import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from 'react';
import { notificationAPI } from '../services/api';
import { useAuth } from './AuthContext';

/**
 * The unread count behind the bell.
 *
 * Held above the bell rather than inside it so the count survives navigation: the bell
 * is rendered by the layout and would refetch on every route change, which is a request
 * per click for a number that changes a few times a day.
 *
 * Polled rather than pushed. The app has no socket, and a notification that arrives a
 * minute late is still waiting when its owner next looks — which is the whole point of
 * storing it. A refetch on window focus covers the case that actually matters: coming
 * back to the tab after doing something else.
 */

const NotificationContext = createContext({
  unread: 0,
  capped: false,
  refresh: () => {},
  markSeen: () => {},
  applyRead: () => {},
});

// Slow enough to be invisible in the request log, quick enough that a hand-off is not
// stale by the time anyone reads it.
const POLL_MS = 60 * 1000;

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [unread, setUnread] = useState(0);
  const [capped, setCapped] = useState(false);
  /*
   * Guards a setState after the provider unmounts.
   *
   * Re-armed on mount, not only cleared on unmount: StrictMode mounts, unmounts and
   * mounts again, so a flag that is only ever cleared stays cleared for the life of the
   * app — and every count that arrives is then dropped, leaving a badge that never
   * appears and an API returning the right answer to nobody.
   */
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);

  const refresh = useCallback(() => {
    if (!isAuthenticated) return Promise.resolve();
    return notificationAPI.unreadCount()
      .then((r) => {
        if (!live.current) return;
        const d = (r.data && r.data.data) || {};
        setUnread(Number(d.count) || 0);
        setCapped(!!d.capped);
      })
      // A count that will not load is not worth an error on screen: the bell simply
      // shows nothing, and the next poll tries again.
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnread(0);
      setCapped(false);
      return undefined;
    }
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    // Coming back to the tab is the moment a stale count is most obvious.
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated, refresh]);

  /*
   * The bell was opened. Clearing the badge is a statement that the rows were SEEN, not
   * that they were read, so the list still shows them as unread until one is opened.
   */
  const markSeen = useCallback(() => notificationAPI.markAllSeen().catch(() => {}), []);

  /*
   * A row was read, or all of them were. Applied locally as well as refetched so the
   * badge answers immediately — a count that lags a click by a round trip reads as a
   * button that did not work.
   */
  const applyRead = useCallback((n) => {
    if (live.current) setUnread((prev) => Math.max(0, prev - (n == null ? prev : n)));
    return refresh();
  }, [refresh]);

  return (
    <NotificationContext.Provider value={{ unread, capped, refresh, markSeen, applyRead }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);

export default NotificationContext;
